import React, { useRef, useState } from "react";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { Icon } from "../../components/Component";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import dayjs from "dayjs";

interface WorkLine {
  id: string;
  workLineNumber: string;
  workLineTitle: string;
  description: string;
  approvedQuantity: number;
  executedQuantity: number;
  alreadyExecutedQty?: number;
  cumulativeExecuted: number;
  percentCompletion: number;
  remainingQuantity: number;
  remarks: string;
  uom: string;
}

interface WorkExecutionBatchPdfGeneratorProps {
  batchData: any;
  workLines: WorkLine[];
  orgLogo?: string;
  orgName?: string;
  triggerAttribute?: string;
  onPdfGenerated?: () => void;
}

const WorkExecutionBatchPdfGenerator: React.FC<WorkExecutionBatchPdfGeneratorProps> = ({
  batchData,
  workLines,
  orgLogo,
  orgName = "Your Organization",
  triggerAttribute,
  onPdfGenerated,
}) => {
  const componentRef = useRef<HTMLDivElement>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string>("");
  const [isGenerating, setIsGenerating] = useState(false);

  const generatePDF = async (download: boolean = false) => {
    console.log("=== Work Execution Batch PDF Generation Started ===");
    console.log("batchData:", batchData);
    console.log("workLines:", workLines);
    console.log("componentRef.current:", componentRef.current);

    if (!componentRef.current) {
      console.error("componentRef.current is null!");
      return;
    }

    try {
      setIsGenerating(true);

      const element = componentRef.current;

      console.log("Element dimensions:", {
        offsetWidth: element.offsetWidth,
        offsetHeight: element.offsetHeight,
        scrollWidth: element.scrollWidth,
        scrollHeight: element.scrollHeight,
        innerHTML_length: element.innerHTML.length,
      });

      // Wait for any pending renders
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Use html2canvas to capture the component
      console.log("Starting html2canvas...");
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        logging: true,
        onclone: (clonedDoc, clonedElement) => {
          console.log("onclone called, clonedElement:", {
            offsetWidth: clonedElement.offsetWidth,
            offsetHeight: clonedElement.offsetHeight,
          });

          // Ensure the cloned element is visible
          clonedElement.style.display = "block";
          clonedElement.style.visibility = "visible";
          clonedElement.style.position = "relative";
          clonedElement.style.left = "0";
          clonedElement.style.top = "0";

          const style = clonedDoc.createElement("style");
          style.innerHTML = `
            * {
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            [data-pdf-template] {
              display: block !important;
              visibility: visible !important;
            }
          `;
          clonedDoc.head.appendChild(style);
        },
      });

      console.log("html2canvas completed. Canvas dimensions:", {
        width: canvas.width,
        height: canvas.height,
      });

      // Create PDF
      const pdf = new jsPDF("p", "mm", "a4");

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      // Calculate dimensions
      const marginTop = 8;
      const marginBottom = 10;
      const marginSide = 5;
      const contentWidth = pdfWidth - marginSide * 2;
      const contentHeight = pdfHeight - marginTop - marginBottom;

      // Convert canvas dimensions to PDF dimensions
      const imgWidth = contentWidth;
      const imgHeight = (canvas.height * contentWidth) / canvas.width;

      // Calculate total pages needed
      const minContentThreshold = 50;
      const basePages = Math.floor(imgHeight / contentHeight);
      const remainingHeight = imgHeight - basePages * contentHeight;
      const remainingPixels = (remainingHeight * canvas.width) / contentWidth;
      const totalPages =
        remainingPixels > minContentThreshold ? basePages + 1 : Math.max(1, basePages);

      console.log("PDF dimensions:", {
        pdfWidth,
        pdfHeight,
        contentWidth,
        contentHeight,
        imgWidth,
        imgHeight,
        totalPages,
        remainingPixels,
      });

      // Process each page
      for (let page = 0; page < totalPages; page++) {
        console.log(`Processing page ${page + 1} of ${totalPages}`);

        if (page > 0) {
          pdf.addPage();
        }

        // Calculate the portion of the image to show on this page
        const sourceY = (page * contentHeight * canvas.width) / contentWidth;
        const sourceHeight = Math.min(
          (contentHeight * canvas.width) / contentWidth,
          canvas.height - sourceY
        );

        console.log(`Page ${page + 1} - sourceY: ${sourceY}, sourceHeight: ${sourceHeight}`);

        // Only render if there's meaningful content
        if (sourceHeight > minContentThreshold) {
          // Create temporary canvas for this page
          const pageCanvas = document.createElement("canvas");
          const pageCtx = pageCanvas.getContext("2d");

          if (pageCtx) {
            // Set canvas size
            pageCanvas.width = canvas.width;
            pageCanvas.height = sourceHeight;

            // Fill with white background
            pageCtx.fillStyle = "#ffffff";
            pageCtx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);

            // Draw the content for this page
            pageCtx.drawImage(
              canvas,
              0,
              sourceY,
              canvas.width,
              sourceHeight,
              0,
              0,
              canvas.width,
              sourceHeight
            );

            // Convert to image data
            const pageImgData = pageCanvas.toDataURL("image/png", 0.95);

            // Calculate PDF dimensions for this page segment
            const pdfContentHeight = (sourceHeight * contentWidth) / canvas.width;

            // Add image to PDF
            console.log(
              `Adding image to PDF - page ${page + 1}, dimensions: ${contentWidth}x${pdfContentHeight}`
            );
            pdf.addImage(
              pageImgData,
              "PNG",
              marginSide,
              marginTop,
              contentWidth,
              pdfContentHeight,
              undefined,
              "FAST"
            );
            console.log(`Image added to page ${page + 1}`);
          }
        }

        // Add page number at the bottom
        pdf.setFontSize(10);
        pdf.setTextColor(128, 128, 128);
        pdf.text(`Page ${page + 1} of ${totalPages}`, pdfWidth - 30, pdfHeight - 5);
      }

      console.log("=== Work Execution Batch PDF Generation Completed ===");

      // Generate blob
      const generatedPdfBlob = pdf.output("blob");
      console.log("PDF Blob size:", generatedPdfBlob.size, "bytes");
      setPdfBlob(generatedPdfBlob);

      // Create URL for preview
      const url = URL.createObjectURL(generatedPdfBlob);
      setPdfUrl(url);

      if (download) {
        // Download the PDF
        pdf.save(
          `WorkExecution_${batchData?.batchNumber || "Batch"}_${dayjs().format("DDMMYYYY")}.pdf`
        );
      }

      setIsGenerating(false);
      return generatedPdfBlob;
    } catch (error) {
      console.error("Error generating PDF:", error);
      setIsGenerating(false);
      throw error;
    }
  };

  const handlePreview = async () => {
    try {
      await generatePDF(false);
      setShowPreview(true);
      // Notify parent that PDF generation is complete
      if (onPdfGenerated) {
        onPdfGenerated();
      }
    } catch (error) {
      console.error("Error generating PDF preview:", error);
      // Also notify on error to hide loading
      if (onPdfGenerated) {
        onPdfGenerated();
      }
    }
  };

  // Helper function to restore body scroll
  const restoreBodyScroll = () => {
    setTimeout(() => {
      document.body.removeAttribute("style");
      document.body.classList.remove("modal-open");
      const backdrops = document.querySelectorAll(".modal-backdrop");
      backdrops.forEach((backdrop) => backdrop.remove());
    }, 300);
  };

  const handleDownload = async () => {
    try {
      if (pdfBlob) {
        const url = URL.createObjectURL(pdfBlob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `WorkExecution_${batchData?.batchNumber || "Batch"}_${dayjs().format(
          "DDMMYYYY"
        )}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        await generatePDF(true);
      }
      setShowPreview(false);
      restoreBodyScroll();
    } catch (error) {
      console.error("Error downloading PDF:", error);
    }
  };

  const closePreview = () => {
    setShowPreview(false);
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl("");
    }
    setPdfBlob(null);
    restoreBodyScroll();
  };

  const getStatusColor = (status: string) => {
    const statusColors: any = {
      Submitted: "#0dcaf0",
      Pending: "#fa8c16",
      Checked: "#fa8c16",
      Verified: "#fa8c16",
      Approved: "#198754",
      Rejected: "#dc3545",
      Returned: "#722ed1",
    };
    return statusColors[status] || "#6c757d";
  };

  const getSlaColor = (age: number): string => {
    if (age <= 1) return "#198754";
    if (age <= 2) return "#fa8c16";
    return "#dc3545";
  };

  const getCompletionColor = (percent: number): string => {
    if (percent === 100) return "#52c41a";
    if (percent >= 75) return "#faad14";
    if (percent >= 50) return "#1890ff";
    return "#ff4d4f";
  };

  const isApproved = batchData?.status === "Approved";

  return (
    <>
      {/* PDF Generation Button */}
      <Button
        className="btn btn-white btn-outline-light"
        onClick={handlePreview}
        disabled={isGenerating}
        {...(triggerAttribute ? { [triggerAttribute]: "true" } : {})}
      >
        <Icon name="download" />
        <span>Export PDF</span>
      </Button>

      {/* PDF Preview Modal */}
      <Modal
        isOpen={showPreview}
        toggle={closePreview}
        size="xl"
        className="modal-dialog-centered"
        backdrop="static"
        scrollable={true}
        onClosed={() => {
          setTimeout(() => {
            document.body.removeAttribute("style");
          }, 100);
        }}
      >
        <ModalHeader toggle={closePreview}>
          <div className="d-flex align-items-center">
            <Icon name="file-docs" className="me-2" />
            Work Execution Batch PDF Preview - {batchData?.batchNumber}
          </div>
        </ModalHeader>
        <ModalBody style={{ padding: 0, maxHeight: "70vh", overflow: "auto" }}>
          {pdfUrl && (
            <iframe
              src={pdfUrl}
              width="100%"
              height="600px"
              style={{ border: "none" }}
              title="PDF Preview"
            />
          )}
        </ModalBody>
        <ModalFooter>
          <div className="d-flex justify-content-between w-100">
            <div className="text-muted small">
              <Icon name="info" className="me-1" />
              Preview generated on {dayjs().format("DD-MMM-YYYY HH:mm")}
            </div>
            <div>
              <Button color="primary" onClick={handleDownload}>
                <Icon name="download" className="me-1" />
                Download PDF
              </Button>
              <Button color="secondary" onClick={closePreview} className="ms-2">
                Close
              </Button>
            </div>
          </div>
        </ModalFooter>
      </Modal>

      {/* Hidden PDF Template */}
      <div
        ref={componentRef}
        data-pdf-template="true"
        style={{
          position: "absolute",
          left: "-9999px",
          top: "0",
          width: "210mm",
          minHeight: "297mm",
          padding: "15px",
          backgroundColor: "white",
          fontFamily: "'Arial', sans-serif",
          fontSize: "11px",
          lineHeight: "1.4",
          color: "#333",
          zIndex: -1000,
        }}
      >
        {/* Header */}
        <div
          style={{
            borderBottom: "2px solid #1890ff",
            paddingBottom: "12px",
            marginBottom: "20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            {orgLogo && (
              <img
                src={orgLogo}
                alt={orgName}
                style={{
                  height: "40px",
                  marginRight: "12px",
                }}
              />
            )}
            <div>
              <h1
                style={{
                  color: "#333",
                  margin: "0",
                  fontSize: "18px",
                  fontWeight: "bold",
                }}
              >
                {orgName}
              </h1>
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <h2
              style={{
                color: "#1890ff",
                margin: "0 0 4px 0",
                fontSize: "16px",
                fontWeight: "bold",
              }}
            >
              WORK EXECUTION BATCH
            </h2>
            <div style={{ fontSize: "12px", fontWeight: "bold" }}>
              {batchData?.batchNumber}
            </div>
            <div style={{ fontSize: "10px", color: "#666" }}>
              <span
                style={{
                  display: "inline-block",
                  backgroundColor: getStatusColor(batchData?.status),
                  color: "white",
                  padding: "2px 8px",
                  borderRadius: "3px",
                  fontSize: "9px",
                }}
              >
                {batchData?.status || "N/A"}
              </span>
            </div>
          </div>
        </div>

        {/* Section 1: Batch Information */}
        <div
          style={{
            marginBottom: "16px",
            pageBreakInside: "avoid",
          }}
        >
          <div
            style={{
              backgroundColor: "#fafafa",
              padding: "8px 12px",
              borderLeft: "3px solid #1890ff",
              marginBottom: "8px",
              fontSize: "13px",
              fontWeight: "bold",
              color: "#333",
            }}
          >
            Batch Information
          </div>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "10px",
              border: "1px solid #f0f0f0",
            }}
          >
            <tbody>
              <tr>
                <td
                  style={{
                    border: "1px solid #f0f0f0",
                    padding: "6px 10px",
                    backgroundColor: "#fafafa",
                    fontWeight: "bold",
                    width: "20%",
                  }}
                >
                  Batch #
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", width: "30%" }}>
                  {batchData?.batchNumber || "-"}
                </td>
                <td
                  style={{
                    border: "1px solid #f0f0f0",
                    padding: "6px 10px",
                    backgroundColor: "#fafafa",
                    fontWeight: "bold",
                    width: "20%",
                  }}
                >
                  Work Order #
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px", width: "30%" }}>
                  {batchData?.woNumber || "-"}
                </td>
              </tr>
              <tr>
                <td
                  style={{
                    border: "1px solid #f0f0f0",
                    padding: "6px 10px",
                    backgroundColor: "#fafafa",
                    fontWeight: "bold",
                  }}
                >
                  Work Order Title
                </td>
                <td colSpan={3} style={{ border: "1px solid #f0f0f0", padding: "6px 10px" }}>
                  {batchData?.workOrderTitle || "-"}
                </td>
              </tr>
              <tr>
                <td
                  style={{
                    border: "1px solid #f0f0f0",
                    padding: "6px 10px",
                    backgroundColor: "#fafafa",
                    fontWeight: "bold",
                  }}
                >
                  Site
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px" }}>
                  {batchData?.siteName || "-"}
                </td>
                <td
                  style={{
                    border: "1px solid #f0f0f0",
                    padding: "6px 10px",
                    backgroundColor: "#fafafa",
                    fontWeight: "bold",
                  }}
                >
                  Project
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px" }}>
                  {batchData?.projectName || "-"}
                </td>
              </tr>
              <tr>
                <td
                  style={{
                    border: "1px solid #f0f0f0",
                    padding: "6px 10px",
                    backgroundColor: "#fafafa",
                    fontWeight: "bold",
                  }}
                >
                  Status
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px" }}>
                  <span
                    style={{
                      display: "inline-block",
                      backgroundColor: getStatusColor(batchData?.status),
                      color: "white",
                      padding: "1px 6px",
                      borderRadius: "3px",
                      fontSize: "9px",
                    }}
                  >
                    {batchData?.status || "-"}
                  </span>
                </td>
                <td
                  style={{
                    border: "1px solid #f0f0f0",
                    padding: "6px 10px",
                    backgroundColor: "#fafafa",
                    fontWeight: "bold",
                  }}
                >
                  SLA Age
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px" }}>
                  <span
                    style={{
                      display: "inline-block",
                      backgroundColor: getSlaColor(batchData?.slaAge || 0),
                      color: "white",
                      padding: "1px 6px",
                      borderRadius: "3px",
                      fontSize: "9px",
                    }}
                  >
                    {batchData?.slaAge || 0} days
                  </span>
                </td>
              </tr>
              <tr>
                <td
                  style={{
                    border: "1px solid #f0f0f0",
                    padding: "6px 10px",
                    backgroundColor: "#fafafa",
                    fontWeight: "bold",
                  }}
                >
                  Submitted On
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px" }}>
                  {batchData?.submittedOn
                    ? dayjs(batchData.submittedOn).format("DD-MMM-YYYY")
                    : "-"}
                </td>
                <td
                  style={{
                    border: "1px solid #f0f0f0",
                    padding: "6px 10px",
                    backgroundColor: "#fafafa",
                    fontWeight: "bold",
                  }}
                >
                  Submitted By
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px" }}>
                  {batchData?.submittedBy || "-"}
                </td>
              </tr>
              <tr>
                <td
                  style={{
                    border: "1px solid #f0f0f0",
                    padding: "6px 10px",
                    backgroundColor: "#fafafa",
                    fontWeight: "bold",
                  }}
                >
                  Total Lines
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px" }}>
                  <span
                    style={{
                      display: "inline-block",
                      backgroundColor: "#1890ff",
                      color: "white",
                      padding: "1px 6px",
                      borderRadius: "3px",
                      fontSize: "9px",
                    }}
                  >
                    {batchData?.totalLines || 0}
                  </span>
                </td>
                <td
                  style={{
                    border: "1px solid #f0f0f0",
                    padding: "6px 10px",
                    backgroundColor: "#fafafa",
                    fontWeight: "bold",
                  }}
                >
                  Total Executed Qty
                </td>
                <td style={{ border: "1px solid #f0f0f0", padding: "6px 10px" }}>
                  <span
                    style={{
                      display: "inline-block",
                      backgroundColor: "#52c41a",
                      color: "white",
                      padding: "1px 6px",
                      borderRadius: "3px",
                      fontSize: "9px",
                    }}
                  >
                    {batchData?.totalExecutedQuantity?.toLocaleString() || 0}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Section 2: Execution Lines */}
        <div
          style={{
            marginBottom: "16px",
            pageBreakInside: "avoid",
          }}
        >
          <div
            style={{
              backgroundColor: "#fafafa",
              padding: "8px 12px",
              borderLeft: "3px solid #1890ff",
              marginBottom: "8px",
              fontSize: "13px",
              fontWeight: "bold",
              color: "#333",
            }}
          >
            Execution Lines ({workLines?.length || 0})
          </div>

          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "8px",
              pageBreakInside: "auto",
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#fafafa" }}>
                <th
                  style={{
                    border: "1px solid #f0f0f0",
                    padding: "6px 4px",
                    textAlign: "left",
                    fontWeight: "bold",
                    width: "10%",
                  }}
                >
                  Work Line #
                </th>
                <th
                  style={{
                    border: "1px solid #f0f0f0",
                    padding: "6px 4px",
                    textAlign: "left",
                    fontWeight: "bold",
                    width: "18%",
                  }}
                >
                  Work Line Title
                </th>
                <th
                  style={{
                    border: "1px solid #f0f0f0",
                    padding: "6px 4px",
                    textAlign: "right",
                    fontWeight: "bold",
                    width: "10%",
                  }}
                >
                  Approved Qty
                </th>
                <th
                  style={{
                    border: "1px solid #f0f0f0",
                    padding: "6px 4px",
                    textAlign: "right",
                    fontWeight: "bold",
                    width: "12%",
                  }}
                >
                  Executed (Till Date)
                </th>
                <th
                  style={{
                    border: "1px solid #f0f0f0",
                    padding: "6px 4px",
                    textAlign: "right",
                    fontWeight: "bold",
                    width: "12%",
                  }}
                >
                  {isApproved ? "Executed (Approved)" : "Executed (Submitted)"}
                </th>
                {!isApproved && (
                  <th
                    style={{
                      border: "1px solid #f0f0f0",
                      padding: "6px 4px",
                      textAlign: "right",
                      fontWeight: "bold",
                      width: "12%",
                    }}
                  >
                    Cumulative (After Approval)
                  </th>
                )}
                <th
                  style={{
                    border: "1px solid #f0f0f0",
                    padding: "6px 4px",
                    textAlign: "center",
                    fontWeight: "bold",
                    width: "10%",
                  }}
                >
                  {isApproved ? "% Completion" : "% (Post Approval)"}
                </th>
                <th
                  style={{
                    border: "1px solid #f0f0f0",
                    padding: "6px 4px",
                    textAlign: "right",
                    fontWeight: "bold",
                    width: "10%",
                  }}
                >
                  {isApproved ? "Remaining" : "Remaining (Post)"}
                </th>
                <th
                  style={{
                    border: "1px solid #f0f0f0",
                    padding: "6px 4px",
                    textAlign: "left",
                    fontWeight: "bold",
                    width: "16%",
                  }}
                >
                  Remarks
                </th>
              </tr>
            </thead>
            <tbody>
              {workLines?.map((line: any, index: number) => {
                const percent = !isApproved
                  ? ((line.cumulativeExecuted / line.approvedQuantity) * 100 * 100) / 100
                  : (line.executedQuantity / line.approvedQuantity) * 100;
                const remaining = !isApproved
                  ? Math.max(0, line.approvedQuantity - line.cumulativeExecuted)
                  : Math.max(0, line.approvedQuantity - (line.alreadyExecutedQty || 0));

                return (
                  <tr key={index} style={{ pageBreakInside: "avoid" }}>
                    <td
                      style={{
                        border: "1px solid #f0f0f0",
                        padding: "5px 4px",
                        fontWeight: "bold",
                      }}
                    >
                      {line.workLineNumber || "-"}
                    </td>
                    <td style={{ border: "1px solid #f0f0f0", padding: "5px 4px" }}>
                      <div style={{ fontWeight: "bold" }}>{line.workLineTitle || "-"}</div>
                      <div style={{ color: "#666", fontSize: "7px" }}>{line.description || ""}</div>
                    </td>
                    <td style={{ border: "1px solid #f0f0f0", padding: "5px 4px", textAlign: "right" }}>
                      <div style={{ fontWeight: "bold" }}>
                        {line.approvedQuantity?.toLocaleString() || 0}
                      </div>
                      <div style={{ color: "#666", fontSize: "7px" }}>{line.uom || ""}</div>
                    </td>
                    <td style={{ border: "1px solid #f0f0f0", padding: "5px 4px", textAlign: "right" }}>
                      <div style={{ fontWeight: "bold" }}>
                        {(line.alreadyExecutedQty || 0)?.toLocaleString()}
                      </div>
                      <div style={{ color: "#666", fontSize: "7px" }}>{line.uom || ""}</div>
                    </td>
                    <td
                      style={{
                        border: "1px solid #f0f0f0",
                        padding: "5px 4px",
                        textAlign: "right",
                        color: "#1890ff",
                      }}
                    >
                      <div style={{ fontWeight: "bold" }}>
                        {line.executedQuantity?.toLocaleString() || 0}
                      </div>
                      <div style={{ color: "#666", fontSize: "7px" }}>{line.uom || ""}</div>
                    </td>
                    {!isApproved && (
                      <td
                        style={{
                          border: "1px solid #f0f0f0",
                          padding: "5px 4px",
                          textAlign: "right",
                          color: "#52c41a",
                        }}
                      >
                        <div style={{ fontWeight: "bold" }}>
                          {line.cumulativeExecuted?.toLocaleString() || 0}
                        </div>
                        <div style={{ color: "#666", fontSize: "7px" }}>{line.uom || ""}</div>
                      </td>
                    )}
                    <td style={{ border: "1px solid #f0f0f0", padding: "5px 4px", textAlign: "center" }}>
                      <span
                        style={{
                          display: "inline-block",
                          backgroundColor: getCompletionColor(line.approvedQuantity === 0 ? 0 : percent),
                          color: "white",
                          padding: "1px 4px",
                          borderRadius: "3px",
                          fontSize: "8px",
                        }}
                      >
                        {line.approvedQuantity === 0 ? 0 : percent}%
                      </span>
                    </td>
                    <td style={{ border: "1px solid #f0f0f0", padding: "5px 4px", textAlign: "right" }}>
                      <div style={{ fontWeight: "bold" }}>{remaining.toLocaleString()}</div>
                      <div style={{ color: "#666", fontSize: "7px" }}>{line.uom || ""}</div>
                    </td>
                    <td style={{ border: "1px solid #f0f0f0", padding: "5px 4px" }}>
                      {line.remarks || "-"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Section 3: Attachments */}
        {batchData?.dms && batchData.dms.length > 0 && (
          <div
            style={{
              marginBottom: "16px",
              pageBreakInside: "avoid",
            }}
          >
            <div
              style={{
                backgroundColor: "#fafafa",
                padding: "8px 12px",
                borderLeft: "3px solid #1890ff",
                marginBottom: "8px",
                fontSize: "13px",
                fontWeight: "bold",
                color: "#333",
              }}
            >
              Attachments ({batchData.dms.length})
            </div>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "10px",
              }}
            >
              <thead>
                <tr style={{ backgroundColor: "#fafafa" }}>
                  <th
                    style={{
                      border: "1px solid #f0f0f0",
                      padding: "6px 8px",
                      textAlign: "left",
                      fontWeight: "bold",
                      width: "5%",
                    }}
                  >
                    #
                  </th>
                  <th
                    style={{
                      border: "1px solid #f0f0f0",
                      padding: "6px 8px",
                      textAlign: "left",
                      fontWeight: "bold",
                      width: "60%",
                    }}
                  >
                    File Name
                  </th>
                  <th
                    style={{
                      border: "1px solid #f0f0f0",
                      padding: "6px 8px",
                      textAlign: "left",
                      fontWeight: "bold",
                      width: "35%",
                    }}
                  >
                    Type
                  </th>
                </tr>
              </thead>
              <tbody>
                {batchData.dms.map((attachment: any, index: number) => (
                  <tr key={index}>
                    <td style={{ border: "1px solid #f0f0f0", padding: "5px 8px" }}>{index + 1}</td>
                    <td
                      style={{ border: "1px solid #f0f0f0", padding: "5px 8px", fontWeight: "bold" }}
                    >
                      {attachment.fileName || "-"}
                    </td>
                    <td style={{ border: "1px solid #f0f0f0", padding: "5px 8px" }}>
                      {attachment.moduleFileType || "Attachment"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer */}
        <div
          style={{
            borderTop: "1px solid #f0f0f0",
            paddingTop: "10px",
            textAlign: "center",
            fontSize: "9px",
            color: "#999",
            marginTop: "20px",
          }}
        >
          Generated on {dayjs().format("DD-MMM-YYYY HH:mm")} | © {dayjs().format("YYYY")} {orgName}
        </div>
      </div>
    </>
  );
};

export default WorkExecutionBatchPdfGenerator;
